import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { getAllRentalVehicleList, addToCartAPI } from '../../services/userServices'
import { type RootState } from '../../store/store'

export interface rentalVehicle {
  id: number
  model_name: string
  model_description: string
  rental_cost_per_day: number
  image_url: string
  vehicle_cat_name: string
  vehicle_subcat_name: string
  discount_rate: number
  is_demand: number
  available_quantity: number
  [key: string]: any
}
interface RentalDetails {
  rental_id: number
  cost_per_km: number
  trained_operator_cost: number
  base_km: number
  base_km_amount: number
  base_location_pincode: string
  shift_hours: number
}
interface UserState {
  loading: boolean
  data: rentalVehicle[]
  error: string | undefined
  cart: Array<{ id: number, count: number }>
  rentalDetails: RentalDetails | null
  startDate: string | null
  endDate: string | null
  startTime: string | null
  endTime: string | null
  eventType: string | null
  pincode: string | null
}


const initialState: UserState = {
  loading: false,
  data: [],
  error: undefined,
  cart: [],
  rentalDetails: null,
  startDate: null,
  endDate: null,
  startTime: null,
  endTime: null,
  eventType: null,
  pincode: null
}
// ✅ Shared final cost calculation
export const calculateFinalCost = (vehicle: rentalVehicle): number => {
  const baseCost = vehicle.rental_cost_per_day || 0
  const discount = vehicle.discount_rate || 0

  if (vehicle.is_demand === 1) {
    // Apply 10% increase, then subtract discount
    const increasedCost = baseCost * 1.1
    return Math.round(increasedCost - discount)
  } else {
    return Math.round(baseCost - discount)
  }
}

export const rentalVehicleSlice = createSlice({
  name: 'rentalVehicle',
  initialState,
  reducers: {
    onQuantityChange: (state, action: PayloadAction<{ id: number, count: number }>) => {
      const { id, count } = action.payload
      const existingItem = state.cart.find(item => item.id === id)
      if (existingItem) {
        if (count <= 0) {
          state.cart = state.cart.filter(item => item.id !== id)
        } else {
          existingItem.count = count
        }
      } else {
        if (count > 0) {
          state.cart.push({ id, count })
        }
      }
    },
    setRentalDetails: (state, action: PayloadAction<RentalDetails>) => {
      state.rentalDetails = action.payload
    },
    setCartEmpty: (state) => {
      state.cart = []
    },
    setRentalPeriod: (
      state,
      action: PayloadAction<{ startDate: string, endDate: string, startTime?: string, endTime?: string, eventType?: string }>
    ) => {
      state.startDate = action.payload.startDate
      state.endDate = action.payload.endDate
      state.startTime = action.payload.startTime || null
      state.endTime = action.payload.endTime || null
      state.eventType = action.payload.eventType || null
    },
    setPincode: (state, action: PayloadAction<string>) => {
      state.pincode = action.payload
    },
    resetRentalState: () => initialState,
  },

  extraReducers: (builder) => {
    builder
      .addCase(getAllRentalVehicleList.pending, (state) => {
        state.loading = true
      })
      .addCase(getAllRentalVehicleList.fulfilled, (state, action) => {
        state.loading = false
        state.data = action.payload
      })
      .addCase(getAllRentalVehicleList.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(addToCartAPI.fulfilled, (state, action) => {
        console.log('✅ API success:', action.payload)
      })
      .addCase(addToCartAPI.rejected, (state, action) => {
        console.error('❌ API failed:', action.payload)
      })
  }
})

export const { onQuantityChange, setRentalDetails, setCartEmpty, setRentalPeriod, setPincode, resetRentalState } = rentalVehicleSlice.actions
export const rentalVehicleSelector = (state: RootState): UserState => state.rentalVehicleReducer
export default rentalVehicleSlice.reducer

export const selectCartVehicles = (state: RootState) => {
  return state.rentalVehicleReducer.cart.map(cartItem => {
    const vehicle = state.rentalVehicleReducer.data.find(v => v.id === cartItem.id)
    return vehicle ? { ...vehicle, count: cartItem.count } : null
  }).filter(Boolean)
}
